import {
  Box,
  Button,
  Flex,
  Link,
  Text,
  Spinner,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
} from '@chakra-ui/react';

import { useAppSelector } from 'hooks';
import { useGroupedBillingSync } from 'hooks/useGroupedBillingSync';
import { useGroupedBinder } from 'hooks/useGroupedBinder';

import type { RootState } from 'store';

import {
  BINDER_STATUS_COMPLETED,
  BINDER_STATUS_ERROR,
} from 'variables/constants';

import StepContainer from './StepContainer';
import StepHeader from './StepHeader';
import StatusBadge from './StatusBadge';
import type { FormStateKey } from './stepTypes';

interface GroupedStepOneProps {
  formStateKey: FormStateKey;
}

const GroupedStepOne = ({ formStateKey }: GroupedStepOneProps) => {
  const uuid = useAppSelector((state: RootState) => state[formStateKey].uuid);
  const { policyGroupId } = useGroupedBillingSync(uuid);

  const {
    binderPdf,
    signing,
    generateBinder,
    isGeneratePending,
    isGenerating,
  } = useGroupedBinder(uuid, policyGroupId);

  const getButtonText = () => {
    if (!binderPdf) return 'Generate Binder';
    if (binderPdf.status === BINDER_STATUS_COMPLETED)
      return 'Re-generate Binder';
    if (binderPdf.status === BINDER_STATUS_ERROR) return 'Generate Binder';
    return 'Generate Binder';
  };

  const getStatusContent = () => {
    if (isGenerating) {
      return (
        <Flex align="center" gap="10px" mb="15px">
          <Spinner size="sm" color="brand.500" />
          <Text fontSize="sm" color="gray.600">
            Generating binder...
          </Text>
        </Flex>
      );
    }

    if (!binderPdf) {
      return null;
    }

    if (binderPdf.status === BINDER_STATUS_COMPLETED && binderPdf.file) {
      return (
        <Box mb="15px">
          <Link
            href={binderPdf.file}
            target="_blank"
            rel="noopener noreferrer"
            color="brand.500"
            textDecoration="underline"
            fontSize="sm"
            fontWeight="semibold"
          >
            Download Binder PDF
          </Link>
        </Box>
      );
    }

    if (binderPdf.status === BINDER_STATUS_ERROR) {
      return (
        <Alert status="error" size="sm" mb="15px">
          <AlertIcon />
          <Box>
            <AlertTitle fontSize="sm">Generation Failed</AlertTitle>
            <AlertDescription fontSize="xs">
              There was an error generating the binder.
            </AlertDescription>
          </Box>
        </Alert>
      );
    }

    return null;
  };

  const isButtonDisabled = () => {
    return isGeneratePending || isGenerating;
  };

  return (
    <StepContainer isEnabled={true}>
      <StepHeader stepNumber={1} title="Generate Binder PDF" isEnabled={true} />

      {getStatusContent()}

      {binderPdf && signing ? (
        <StatusBadge status="success">Binder sent for signing</StatusBadge>
      ) : (
        <Button
          onClick={generateBinder}
          disabled={isButtonDisabled()}
          isLoading={isGeneratePending}
          variant="brand"
          size="sm"
          w="100%"
        >
          {getButtonText()}
        </Button>
      )}
    </StepContainer>
  );
};

export default GroupedStepOne;
